<template>
    <div class="ppv2-mx-auto ppv2-p-4">
        <!-- Confirmation Dialog -->
        <ConfirmationDialog
            :is-open="showDeleteConfirm"
            :title="$t('common.confirm_delete')"
            :message="$t('establishment.scheduling.confirm_delete_holiday')"
            type="danger"
            confirm-button-variant="danger"
            @confirm="confirmDelete"
            @cancel="cancelDelete"
        />

        <!-- Add Week Panel -->
        <PanelComponent
            v-if="showAddPanel"
            :title="$t('resource_planning.exceptions.set_special_hours')"
            :width="'800px'"
            @close="closeAddPanel"
            @validate="confirmHolidayWeeks"
            :disabledConfirm="!isValid"
        >
            <WeekRangePlanning
                ref="weekRangePlanner"
                @save="handleSaveHolidayWeeks"
                class="ppv2-mt-4"
                :mode="mode"
                :selectedSchedule="selectedSchedule"
            />

            <!-- <div class="footer-actions">
                <button type="button" @click="closePanel" class="btn-cancel">
                  {{ $t('common.cancel') }}
                </button>
                <button type="submit" class="btn-save">
                  {{ $t('resource_planning.holiday.save') }}
                </button>
            </div> -->
        </PanelComponent>

        <!-- Header -->
        <div class="ppv2-flex ppv2-flex-wrap ppv2-justify-between ppv2-items-center ppv2-mb-6">
            <div>
                <h1 class="header-title">{{ $t('resource_planning.holiday.title') }}</h1>
                <p class="header-description">{{ $t('resource_planning.holiday.description') }}</p>
            </div>
            <button class="add-button" @click="addNewSchedule">
                <div class="button-content">
                    <div class="button-text">
                        {{ $t('resource_planning.holiday.add_button') }}
                    </div>
                </div>
            </button>
        </div>

        <!-- Empty State -->
        <div v-if="schedules.length === 0" class="ppv2-bg-white ppv2-rounded-lg ppv2-shadow ppv2-border ppv2-border-gray-200">
            <div class="ppv2-text-center ppv2-py-12">
                <svg
                    class="ppv2-mx-auto ppv2-h-12 ppv2-w-12 ppv2-text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.5"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                </svg>
                <h3 class="ppv2-mt-2 ppv2-text-sm poppinsMedium">
                    {{ $t('resource_planning.holiday.no_holidays') }}
                </h3>
                <p class="ppv2-mt-1 ppv2-text-sm ppv2-text-gray-500 mb-0">
                    {{ $t('resource_planning.holiday.no_holidays_description') }}
                </p>
                <div class="ppv2-mt-6">
                    <button class="add-button" @click="addNewSchedule">
                        <div class="button-content">
                            <div class="button-text">
                                {{ $t('resource_planning.holiday.add_button') }}
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </div>

        <!-- Schedule List -->
        <div v-else :key="'schedule-list-' + $i18n.locale" class="ppv2-space-y-4">
            <div v-for="(schedule, weekIndex) in paginatedSchedules" :key="'week-' + weekIndex" class="schedule-card">
                <!-- Week Days -->
                <div class="ppv2-flex ppv2-overflow-x-auto ppv2-w-full">
                    <div class="ppv2-w-[200px] ppv2-text-center">
                        <div class="week-info-box">
                            <div class="ppv2-flex ppv2-items-center week-icons">
                                <div class="calendar-icon">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="ppv2-h-6 ppv2-w-6 ppv2-text-[#0f172a]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                                <div class="week-title">{{ $t('resource_planning.week_number') }}{{ schedule.weekNumber }}</div>
                                <div class="year-div">{{ schedule.year }}</div>
                            </div>

                            <div class="week-subtitle">
                                {{ schedule.title }}
                            </div>
                            <div class="ppv2-flex ppv2-items-end ppv2-justify-end">
                                <!-- Adjust height as needed -->
                                <!-- Your edit button -->
                                <button class="delete-button" @click="deleteSchedule(schedule)">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="ppv2-h-5 ppv2-w-5 text-red-600"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M6 2a1 1 0 00-1 1v1H3.5a.5.5 0 000 1h.638l.605 9.05A2 2 0 006.736 16h6.528a2 2 0 001.993-1.95l.605-9.05h.638a.5.5 0 000-1H15V3a1 1 0 00-1-1H6zm1 4a.5.5 0 011 0v7a.5.5 0 01-1 0V6zm4 0a.5.5 0 00-1 0v7a.5.5 0 001 0V6z"
                                            clip-rule="evenodd"
                                        />
                                    </svg>
                                </button>

                                <button class="edit-button" @click="editSchedule(schedule)">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="ppv2-h-5 ppv2-w-5"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="ppv2-flex ppv2-flex-grow ppv2-shrink-0">
                        <div
                            v-for="(day, dayIndex) in schedule.days"
                            :key="'week-' + weekIndex + '-day-' + dayIndex"
                            :class="
                                'ppv2-flex-1 day-box ' +
                                (day.isClosed || day.rawData.periods.length == 0 ? 'day-box-closed' : 'day-box-open')
                            "
                        >
                            <div class="ppv2-flex ppv2-justify-between">
                                <div class="day-name">{{ day.name }}</div>
                                <div class="day-date">{{ day.date }}</div>
                            </div>

                            <div class="day-hours">
                                <template v-if="day.isClosed">
                                    <div>Fermé</div>
                                    <div class="ppv2-mt-1">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="ppv2-h-4 ppv2-w-4 ppv2-mx-auto"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                            />
                                        </svg>
                                    </div>
                                </template>
                                <template v-else>
                                    <div>{{ day.hours }}</div>
                                    <div
                                        v-if="day.rawData.lunches && day.rawData.lunches.length > 0"
                                        class="ppv2-text-xs ppv2-text-gray-500"
                                    >
                                        Pause: {{ day.rawData.lunches[0].start }} - {{ day.rawData.lunches[0].end }}
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="schedules.length > itemsPerPage" class="ppv2-flex ppv2-justify-center ppv2-mt-4 ppv2-pb-4">
            <div class="pagination">
                <button
                    @click="currentPage--"
                    :disabled="currentPage === 1"
                    class="pagination-button"
                    :class="{ disabled: currentPage === 1 }"
                >
                    &laquo;
                </button>

                <span class="pagination-info"> Page {{ currentPage }} sur {{ totalPages }} </span>

                <button
                    @click="currentPage++"
                    :disabled="currentPage >= totalPages"
                    class="pagination-button"
                    :class="{ disabled: currentPage >= totalPages }"
                >
                    &raquo;
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import ApiEstablishmentManager from '../../../helpers/ApiEstablishmentManager'
import PanelComponent from '../../Shared/PanelComponent.vue'
import WeekRangePlanning from './WeekRangePlanning.vue'
import ConfirmationDialog from '../../Shared/ConfirmationDialog.vue'

export default {
    components: {
        PanelComponent,
        WeekRangePlanning,
        ConfirmationDialog,
    },
    name: 'HolidayScheduling',
    data() {
        return {
            isLoading: false,
            showAddPanel: false,
            newWeekStartDate: '',
            minDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD
            schedules: [],
            selectedSchedule: null,
            mode: 'add',
            establishmentId: 1,
            dataToSave: null,
            showDeleteConfirm: false,
            scheduleToDelete: null,
            isValid: true,
            // Pagination
            currentPage: 1,
            itemsPerPage: 5, // Show 5 weeks per page
        }
    },
    created() {
        this.loadSchedules()
    },
    computed: {
        dayNames() {
            return [
                this.$t('resource_planning.week_days_schedule.mon'),
                this.$t('resource_planning.week_days_schedule.tue'),
                this.$t('resource_planning.week_days_schedule.wed'),
                this.$t('resource_planning.week_days_schedule.thu'),
                this.$t('resource_planning.week_days_schedule.fri'),
                this.$t('resource_planning.week_days_schedule.sat'),
                this.$t('resource_planning.week_days_schedule.sun'),
            ]
        },
        // Pagination computed properties
        totalPages() {
            return Math.ceil(this.schedules.length / this.itemsPerPage)
        },
        paginatedSchedules() {
            const start = (this.currentPage - 1) * this.itemsPerPage
            const end = start + this.itemsPerPage
            return this.schedules.slice(start, end)
        },
    },
    watch: {
        schedules() {
            // Reset to first page when schedules change
            this.currentPage = 1
        },
        '$i18n.locale': {
            immediate: true,
            handler() {
                // Force a complete re-render of the component
                this.$nextTick(() => {
                    this.$forceUpdate()
                    this.loadSchedules()
                })
            },
        },
    },
    methods: {
        // Fake API call to get planning data
        async fetchPlanningData() {
            // Simulate API delay
            const today = new Date()
            const nextFourYears = new Date(today)
            nextFourYears.setDate(today.getDate() + 1000)
            const schedule = await ApiEstablishmentManager.getHolidayScheduling({
                establishmentId: this.establishmentId,
                start: today.toISOString().split('T')[0],
                end: nextFourYears.toISOString().split('T')[0],
            })
            // console.log('schedule of nextFourYears ', nextFourYears, schedule)
            // Current date for reference

            // Generate data for current week and next week
            return schedule.data
        },

        // Helper to format date as YYYY-MM-DD
        formatDate(date) {
            const d = new Date(date)
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0') // Months are 0-based
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        },

        // Load schedules from API
        async loadSchedules() {
            this.isLoading = true
            try {
                const apiData = await this.fetchPlanningData()
                this.schedules = this.prepareSchedules(apiData)
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoading = false
            }
        },

        getISOWeekYear(date) {
            const tmpDate = new Date(date.getTime())
            tmpDate.setDate(tmpDate.getDate() + 4 - (tmpDate.getDay() || 7))
            return tmpDate.getFullYear()
        },
        prepareSchedules(apiData) {
            if (!Array.isArray(apiData) || apiData.length === 0) return []

            const dayNames = [
                this.$t('resource_planning.week_days_schedule.mon'),
                this.$t('resource_planning.week_days_schedule.tue'),
                this.$t('resource_planning.week_days_schedule.wed'),
                this.$t('resource_planning.week_days_schedule.thu'),
                this.$t('resource_planning.week_days_schedule.fri'),
                this.$t('resource_planning.week_days_schedule.sat'),
                this.$t('resource_planning.week_days_schedule.sun'),
            ]

            const weeks = {}
            const label = { weekKey: null, label: null }

            for (const item of apiData) {
                const date = new Date(item.date)
                const weekNum = this.getWeekNumber(date)
                const yearNum = this.getISOWeekYear(date)
                const weekKey = `${yearNum}-${weekNum}`

                if (label.weekKey != weekKey || item.label) {
                    label.weekKey = weekKey
                    label.label = item.label
                    if (weeks[weekKey] && weeks[weekKey].title.startsWith('Semaine') && item.label) {
                        weeks[weekKey].title = item.label
                    }
                }
                const noPlanningConfig = !item.start || !item.end

                // Adjust getDay so Monday = 0, Sunday = 6
                const jsDay = date.getDay() // 0 (Sun) - 6 (Sat)
                const dayOfWeek = jsDay === 0 ? 6 : jsDay - 1

                if (!weeks[weekKey]) {
                    weeks[weekKey] = {
                        weekNumber: weekNum,
                        year: yearNum,
                        title: label.label ?? 'Semaine ' + weekNum,
                        days: Array(7)
                            .fill(null)
                            .map((_, i) => ({
                                name: dayNames[i],
                                date: '',
                                hours: 'Fermé',
                                isClosed: true,
                                rawData: null,
                            })),
                    }
                }
                // console.log('noPlanningConfig', noPlanningConfig, String(date.getDate()).padStart(2, '0'))
                if (noPlanningConfig) {
                    weeks[weekKey].days[dayOfWeek] = {
                        name: dayNames[dayOfWeek],
                        date: String(date.getDate()).padStart(2, '0'),
                        hours: this.$t('resource_planning.unplanned_hours'),
                        isClosed: false,
                        isDefault: true,
                        rawData: {
                            date: item.date,
                            periods: [],
                            lunches: [],
                        },
                    }
                } else {
                    const isOpen = item.is_open && item.start !== item.end
                    const hours = isOpen ? `${item.start} - ${item.end}` : 'Fermé'

                    weeks[weekKey].days[dayOfWeek] = {
                        name: dayNames[dayOfWeek],
                        date: String(date.getDate()).padStart(2, '0'),
                        hours,
                        isClosed: !isOpen,
                        isDefault: false,
                        rawData: {
                            date: item.date,
                            periods: [{ start: item.start, end: item.end }],
                            lunches:
                                item.lunch_start !== item.lunch_end ? [{ start: item.lunch_start, end: item.lunch_end }] : [],
                        },
                    }
                }
            }

            return Object.values(weeks).sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year
                return a.weekNumber - b.weekNumber
            })
        },

        getWeekNumber(date) {
            const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
            const dayNum = d.getUTCDay() || 7
            d.setUTCDate(d.getUTCDate() + 4 - dayNum)
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
            return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
        },

        // Open the add new week panel
        addNewSchedule() {
            this.showAddPanel = true
            this.mode = 'add'
            this.selectedSchedule = null
            // Set default start date to next Monday if today is weekend, otherwise next week Monday
            const today = new Date()
            const nextMonday = new Date(today)
            nextMonday.setDate(today.getDate() + ((1 + 7 - today.getDay()) % 7 || 7))
            this.newWeekStartDate = nextMonday.toISOString().split('T')[0]
        },

        // editSchedule
        editSchedule(schedule) {
            this.showAddPanel = true
            this.selectedSchedule = schedule
            this.mode = 'edit'
        },
        deleteSchedule(schedule) {
            this.scheduleToDelete = schedule
            this.showDeleteConfirm = true
        },

        cancelDelete() {
            this.showDeleteConfirm = false
            this.scheduleToDelete = null
        },

        async confirmDelete() {
            if (!this.scheduleToDelete) return

            try {
                const { weekNumber, year } = this.scheduleToDelete
                // Step 1: Get ISO week start (Monday)
                const monday = ApiEstablishmentManager.getMondayOfISOWeek(weekNumber, year)
                const sunday = new Date(monday.getTime())
                sunday.setDate(sunday.getDate() + 6)

                const start = this.formatDate(monday)
                const end = this.formatDate(sunday)

                // Show loading state if needed
                this.isLoading = true

                // Call the API to delete the schedule
                await ApiEstablishmentManager.deleteScheduling(this.establishmentId, {
                    start,
                    end,
                })

                // Show success message
                this.$toast.success(this.$t('establishment.scheduling.delete_success'))

                // Reload the schedules after successful deletion
                await this.loadSchedules()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error deleting schedule:', error)
                this.$toast.error(this.$t('common.error_occurred'))
            } finally {
                this.isLoading = false
                this.showDeleteConfirm = false
                this.scheduleToDelete = null
            }
        },

        // Close the add panel
        closeAddPanel() {
            this.showAddPanel = false
            this.dataToSave = null
            this.selectedSchedule = null
        },

        // Format date for display (e.g., "01/01/2023")
        formatDisplayDate(date) {
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            })
        },

        // Handle saving holiday weeks
        async confirmHolidayWeeks() {
            try {
                const response = await ApiEstablishmentManager.setScheduling(this.establishmentId, this.dataToSave)
                this.closeAddPanel()
                await this.loadSchedules()

                // Show success message
                this.$toast.success(
                    this.mode == 'add'
                        ? this.$t('establishment.scheduling.save_success')
                        : this.$t('establishment.scheduling.update_success')
                )
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving holiday weeks:', error)
                this.$toast.error(this.$t('common.error_occurred'))
            }
        },

        handleSaveHolidayWeeks(weeksData, isValid) {
            this.isValid = isValid
            this.dataToSave = weeksData
            return
        },

        // Method to update a day's schedule
        updateDaySchedule(weekIndex, dayIndex, newData) {
            if (!this.schedules[weekIndex] || !this.schedules[weekIndex].days[dayIndex]) return

            // Update the day's data
            const day = this.schedules[weekIndex].days[dayIndex]
            day.rawData = newData
            day.isClosed = !newData.periods || newData.periods.length === 0

            // Update hours display
            if (newData.periods && newData.periods.length > 0) {
                const first = newData.periods[0]
                const last = newData.periods[newData.periods.length - 1]
                day.hours = `${first.start} - ${last.end}`
            } else {
                day.hours = 'Fermé'
            }

            // Trigger reactivity
            this.schedules = [...this.schedules]
        },

        // Format time periods for display
        formatTimePeriod(periods) {
            if (!periods || periods.length === 0) return 'Fermé'
            const first = periods[0]
            const last = periods[periods.length - 1]
            return `${first.start} - ${last.end}`
        },
    },
}
</script>

<style scoped>
/* Pagination Styles */
.pagination {
    @apply ppv2-flex ppv2-items-center ppv2-gap-2;
}

.pagination-button {
    @apply ppv2-px-3 ppv2-py-1 ppv2-border ppv2-rounded ppv2-bg-white ppv2-text-gray-700 ppv2-border-gray-300;
    @apply hover:ppv2-bg-gray-50 focus:ppv2-outline-none focus:ppv2-ring-2 focus:ppv2-ring-blue-500 focus:ppv2-border-blue-500;
    @apply disabled:ppv2-opacity-50 disabled:ppv2-cursor-not-allowed;
    min-width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.pagination-info {
    @apply ppv2-px-3 ppv2-py-1 ppv2-text-sm ppv2-text-gray-600;
}

.pagination-button:not(.disabled):hover {
    @apply ppv2-bg-gray-100;
}

.pagination-button.disabled {
    @apply ppv2-opacity-50 ppv2-cursor-not-allowed;
}

.header-title {
    @apply ppv2-text-2xl ppv2-font-bold ppv2-text-gray-900;
}

.header-description {
    @apply ppv2-mt-1 ppv2-text-sm ppv2-text-gray-500;
}

.add-button {
    @apply ppv2-bg-transparent ppv2-border-none ppv2-cursor-pointer ppv2-p-0;
    outline: none;
}

.add-button .button-content {
    width: 100%;
    height: 100%;
    padding: 10px;
    border-radius: 36px;
    outline: 2px solid #0f172a;
    outline-offset: -1px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    background: white;
    transition: all 0.2s ease;
}

.add-button .button-text {
    color: #021832;
    font-size: clamp(12px, 0.729545vw, 16px);
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    line-height: 19px;
    word-wrap: break-word;
}

.add-button:hover .button-content {
    background-color: #0f172a;
    outline-color: #0f172a;
}
.add-button:hover .button-text {
    color: #fff;
}
.add-button:active .button-content {
    background-color: #f1f3f5;
}
.footer-actions {
    @apply ppv2-flex ppv2-justify-end ppv2-space-x-3 ppv2-mt-6 ppv2-pt-4 ppv2-border-t ppv2-border-gray-200;
}

.btn-cancel {
    @apply ppv2-px-4 ppv2-py-2 ppv2-text-sm ppv2-font-medium ppv2-text-gray-700 ppv2-bg-white ppv2-border ppv2-border-gray-300 ppv2-rounded-md ppv2-shadow-sm hover:ppv2-bg-gray-50 focus:ppv2-outline-none focus:ppv2-ring-2 focus:ppv2-ring-offset-2 focus:ppv2-ring-blue-500;
}

.btn-save {
    @apply ppv2-inline-flex ppv2-justify-center ppv2-px-4 ppv2-py-2 ppv2-text-sm ppv2-font-medium ppv2-text-white ppv2-bg-blue-600 ppv2-border ppv2-border-transparent ppv2-rounded-md ppv2-shadow-sm hover:ppv2-bg-blue-700 focus:ppv2-outline-none focus:ppv2-ring-2 focus:ppv2-ring-offset-2 focus:ppv2-ring-blue-500;
}

.schedule-card {
    @apply ppv2-bg-white;
}

.dark .ppv2-bg-white {
    background-color: rgba(255, 255, 255, 0.1);
}
.week-info-box {
    background-color: rgba(98, 194, 246, 0.15);
    @apply ppv2-p-2 ppv2-items-center ppv2-justify-between;
}

.calendar-icon {
}

.week-title {
    font-size: 16px;
    font-weight: bold;
    color: #0f172a;
    margin-left: 5px;
}

.week-subtitle {
    text-align: left;
    font-size: clamp(12px, 0.729545vw, 16px);
    font-weight: bold;
    color: #0f172a;
    margin-top: 15px;
}

.edit-button {
    @apply ppv2-p-2 ppv2-text-gray-500 hover:ppv2-text-gray-700;
}
.delete-button {
    @apply ppv2-p-2 ppv2-text-[#ed4b9e] hover:ppv2-text-[#ed4b9e];
}

.day-box {
    min-width: 110px;
    border: 1px solid #f4f4f4;
    padding: 8px;
}
.day-box.day-box-open {
    opacity: 100%;
}
.day-box.day-box-closed {
    background-color: #e5e5e5;
    opacity: 50%;
}

.day-name {
    font-size: clamp(12px, 0.729545vw, 16px);
}

.day-date {
    font-size: 14;
    font-weight: bold;
    @apply ppv2-mb-2;
}

.day-hours {
    @apply ppv2-text-sm ppv2-h-10 ppv2-flex ppv2-flex-col ppv2-justify-center;
}

.week-icons {
    position: relative;
}
.year-div {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #0f172a;
    color: #ffff;
    padding: 2px;
    opacity: 80%;
}
</style>
